import { Color, Fog, Vector3 } from 'three';
import type { System } from '@/core/system';
import type { Engine } from '@/core/engine';
import type { Time } from '@/core/time';
import { City } from '@/world/city';
import { Lighting } from '@/rendering/lighting';
import { BuildingField } from '@/rendering/building-field';
import { PALETTE } from '@/config';
import { damp, lerp, clamp01 } from '@/utils/math';
import type { PreferenceVector } from '@/core/types';

/**
 * Translates the city's inner state into atmosphere. This is where the emotional
 * arc lives: as the city grows more alive it warms and clears; as the player
 * stagnates it greys, the fog closes in, the windows go dark. Preferences colour
 * the mood too — a contemplative player drifts the world toward a hushed dusk with
 * lit windows; an intimacy-seeker gets a tighter, foggier enclosure.
 *
 * All transitions are eased over seconds, never cut, so the mood breathes.
 */
export class EnvironmentController implements System {
  readonly name = 'environment';

  private mood = 0.6;
  private night = 0;
  private glow = 0;

  /** Exposed for atmosphere layers (sky, mountains, motes) wired in main. */
  get moodValue(): number {
    return this.mood;
  }
  get nightValue(): number {
    return this.night;
  }

  // Atmosphere layers are attached structurally (duck-typed) so this controller
  // never imports the rendering files — they stay swappable and optional.
  private atmosphere: {
    sky?: { update(elapsed: number, camPos: Vector3, mood: number, night: number): void };
    mountains?: { update(mood: number, night: number): void };
    motes?: { update(dt: number, elapsed: number, playerPos: Vector3, night: number): void };
  } = {};

  attachAtmosphere(a: EnvironmentController['atmosphere']): void {
    this.atmosphere = a;
  }
  private fogNear = 40;
  private fogFar = 260;
  private prefs: PreferenceVector | null = null;

  private readonly fogAlive = new Color(PALETTE.moodAlive);
  private readonly fogBleak = new Color(PALETTE.moodBleak);
  private readonly skyAlive = new Color(PALETTE.sky);
  private readonly skyBleak = new Color(PALETTE.moodBleak);
  private readonly tmp = new Color();

  constructor(
    private engine: Engine,
    private city: City,
    private lighting: Lighting,
    private buildings: BuildingField,
    private playerPos: () => Vector3,
  ) {}

  init(): void {
    this.engine.events.on('ai:preferences', (p) => {
      this.prefs = p;
    });
  }

  update(time: Time): void {
    const dt = time.delta;
    const v = this.city.vitality;
    const m = this.city.maturity;
    const p = this.prefs;

    // Mood follows vitality, lifted a little by overall maturity (an evolved city
    // feels more intentional, more alive).
    const moodTarget = clamp01(v * 0.85 + m * 0.2);
    this.mood = damp(this.mood, moodTarget, 0.6, dt);

    // Nocturnality grows with contemplation and maturity; intimacy nudges it too.
    const nightTarget = p ? clamp01(p.contemplation * 0.6 + p.intimacy * 0.2 + m * 0.35) : m * 0.35;
    this.night = damp(this.night, nightTarget, 0.4, dt);

    // Window glow: bright at night, and generally as the city matures.
    const glowTarget = clamp01(this.night * 0.9 + m * 0.25);
    this.glow = damp(this.glow, glowTarget, 0.5, dt);
    this.buildings.material.uniforms.uGlowAmount.value = this.glow;

    // Lighting.
    this.lighting.setMood(this.mood, this.night);
    const pos = this.playerPos();
    this.lighting.follow(pos.x, pos.z);

    // Fog closes in when bleak or when the player favours intimacy.
    const intim = p ? p.intimacy : 0;
    const nearTarget = lerp(60, 22, clamp01((1 - this.mood) * 0.6 + intim * 0.6));
    const farTarget = lerp(300, 130, clamp01((1 - this.mood) * 0.7 + intim * 0.4));
    this.fogNear = damp(this.fogNear, nearTarget, 0.5, dt);
    this.fogFar = damp(this.fogFar, farTarget, 0.5, dt);

    const fog = this.engine.scene.fog as Fog;
    this.tmp.copy(this.fogBleak).lerp(this.fogAlive, this.mood);
    // Night tints everything cooler and darker.
    this.tmp.multiplyScalar(lerp(1, 0.72, this.night));
    fog.color.copy(this.tmp);
    fog.near = this.fogNear;
    fog.far = this.fogFar;

    (this.engine.scene.background as Color)
      .copy(this.skyBleak)
      .lerp(this.skyAlive, this.mood)
      .multiplyScalar(lerp(1, 0.6, this.night));

    // A touch more bloom at night to make the lit windows bloom softly.
    this.engine.bloom.intensity = lerp(0.5, 0.95, this.night) * lerp(0.7, 1, this.mood);

    // Drive the attached atmosphere layers with the same eased mood/night.
    const cam = this.engine.camera.position;
    this.atmosphere.sky?.update(time.elapsed, cam, this.mood, this.night);
    this.atmosphere.mountains?.update(this.mood, this.night);
    this.atmosphere.motes?.update(dt, time.elapsed, pos, this.night);
  }
}
